import * as React from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';

import { PopperContainer } from '@/atoms/PopperContainer';
import { Popper } from '@/components/Popper/Popper';
import { Divide } from '@/features/ManageContacts/components/Divide';
import { PriorityIcon as PriorityIconImpl } from '@/features/ManageContacts/components/PriorityIcon';
import { Priority as PriorityList } from '@/features/ManageContacts/components/PriorityList';
import { usePeople } from '@/features/ManageContacts/hooks/queries/usePeople';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useForkRef } from '@/hooks/useForkRef';
import { useIsSharedFeed } from '@/hooks/useIsSharedFeed';
import { useModal } from '@/hooks/useModal';
import RightIcon from '@/icons/expand_right.svg?react';
import { SETTINGS_ROUTES } from '@/nav-routes';
import type { Contact, People } from '@/types';
import { getLogo } from '@/utils/getIcons';

type ContactInfoType = {
  children?: React.ReactNode;
  contacts?: string[];
  source: string;
  title?: React.ReactNode;
  isChannel?: boolean;
};

type FromType = Omit<React.HTMLAttributes<HTMLDivElement>, 'css'> & {
  children: React.ReactNode;
  hasPopup?: boolean;
  priority?: string;
  isChannel?: boolean;
};

type HeaderType = {
  people: People;
};

type ContactsType = {
  contacts: Contact[];
};

type ManageContactType = {
  peopleId: string;
};

export type ContactInfoWrapperType = {
  /**
   * contacts for title Info
   */
  title?: React.ReactNode;
  /**
   * emails for Email Info
   */
  contact?: React.ReactNode;
};

const iconStyle = css`
  ${tw`w-3 h-3 flex-shrink-0`}
`;

const PriorityIcon = styled(PriorityIconImpl)`
  ${iconStyle}
`;

const Info = React.forwardRef<HTMLElement, FromType>(
  ({ children, hasPopup, priority, onClick, ...rest }, ref) => {
    const { mobile } = useResponsiveConstants();

    const isInteractable = !mobile && hasPopup;
    const Comp = isInteractable ? 'button' : 'div';

    return (
      <Comp
        // @ts-expect-error since this component can either be a button or div
        ref={ref}
        className={`flex items-center gap-1 w-full overflow-hidden ${
          isInteractable ? 'sub-heading-xs cursor-pointer text-emerald-900' : ''
        }`}
        {...(isInteractable && { onClick })}
        {...rest}
      >
        <span className='truncate flex-shrink'>{children}</span>
        {hasPopup ? <PriorityIcon priority={priority} /> : null}
      </Comp>
    );
  }
);

const Header = ({ people }: HeaderType) => {
  return (
    <div className='flex justify-between items-center gap-2'>
      <div className='flex-1 flex items-center gap-2'>
        <h3 className='headline-small-bold text-gray-700'>
          {people.people_name}
        </h3>
      </div>
      <div className='flex-shrink-0'>
        <PriorityList disablePortal people={people} />
      </div>
    </div>
  );
};

const ContactRow = ({ contact_name, contact_source }: Contact) => {
  const Icon = getLogo(contact_source);

  return (
    <div className='flex items-center gap-2'>
      <span className='w-4 h-4'>
        <Icon />
      </span>
      <span className='text-gray-700 text-xs'>{contact_name}</span>
    </div>
  );
};

const ManageContact = ({ peopleId }: ManageContactType) => {
  return (
    <a
      className='w-max'
      target='_blank'
      rel='noreferrer noopener'
      href={`${SETTINGS_ROUTES.CONTACTS}/${peopleId}`}
    >
      <span className='flex justify-between items-center p-2 gap-1'>
        <span className='text-xs text-gray-900'>Manage Contact</span>
        <span className='w-4 h-4 fill-current text-gray-700 flex-shrink-0'>
          <RightIcon />
        </span>
      </span>
    </a>
  );
};

const Contacts = ({ contacts }: ContactsType) => {
  return (
    <div className='p-2'>
      <div className='flex flex-col gap-1'>
        {contacts.map((contact) => (
          <ContactRow key={contact.contact_id} {...contact} />
        ))}
      </div>
    </div>
  );
};

const ContactInfo = React.forwardRef<HTMLDivElement, ContactInfoType>(
  (
    { contacts = [], source, title, children, isChannel, ...rest },
    parentRef
  ) => {
    const { isSharedData } = useIsSharedFeed({ isChannel });

    const anchor = React.useRef<HTMLDivElement | null>(null);
    const forkedRef = useForkRef(parentRef, anchor);

    const { data, isError, isLoading } = usePeople(contacts, source);
    const { showModal, openModal, closeModal } = useModal();

    if (!data || !data[0] || isError || isLoading || isSharedData) {
      return (
        <Info ref={forkedRef} {...rest}>
          {children}
        </Info>
      );
    }

    return (
      <>
        <Info
          ref={forkedRef}
          hasPopup
          onClick={openModal}
          priority={data[0].people_priority}
          {...rest}
        >
          {children}
        </Info>
        <Popper
          targetRef={anchor}
          isVisible={showModal}
          closeOnOutSideClick
          closePopper={closeModal}
        >
          <PopperContainer>
            <Header people={data[0]} />
            <Divide />
            <Contacts contacts={data[0].contacts} />
            <ManageContact peopleId={data[0].people_id} />
          </PopperContainer>
        </Popper>
      </>
    );
  }
);

const ContactInfoWrapper: React.FunctionComponent<ContactInfoWrapperType> = ({
  title,
  contact,
}) => {
  return (
    <div className='flex'>
      <div className='mr-2 w-auto'>{title}</div>
      {contact}
    </div>
  );
};

export type { ContactInfoType };
export { ContactInfo, ContactInfoWrapper };
